import React, { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { PenLine, Plus, ArrowRight, Share2 } from 'lucide-react';
import { useAppData } from '../context/AppDataContext';
import CoachingCard from '../components/CoachingCard';
import ProgressQuickInput from '../components/ProgressQuickInput';
import PaceLine from '../components/PaceLine';
import PageModeSwitch from '../components/PageModeSwitch';
import ReadingCertification from '../components/ReadingCertification';
import ShareModal from '../components/ShareModal';
import { buildAvailableTemplates } from '../utils/shareTemplates';
import RecentRecordsFeed from '../components/records/RecentRecordsFeed';
import { BOOK_STATUS, getBookStatus } from '../constants/bookStatus';

const Home = () => {
  const { onOpenQuickRecord } = useOutletContext();
  const {
    books, completedBooks, today, todayStats, coaching,
    records, recordCounts, streak, userSettings
  } = useAppData();
  const [shareOpen, setShareOpen] = useState(false);
  // 입력 중인 쪽수 (bookId → 쪽). 저장 전에도 인증 문구가 미리 보이게 한다
  const [drafts, setDrafts] = useState({});

  // 지금 데이터로 만들 수 있는 공유 문구만 추린다
  const shareTemplates = useMemo(() => buildAvailableTemplates({
    books, completedBooks, records, today, streak,
    bookById: new Map(books.map(b => [b.id, b]))
  }), [books, completedBooks, records, today, streak]);

  const readingBooks = books.filter(b => getBookStatus(b) === BOOK_STATUS.READING);
  // 구글 계정의 실명이 아니라 설정에서 정한 이름을 쓴다 (화면을 남에게 보여줄 때가 있다)
  const nickname = userSettings?.nickname || '독자';

  return (
    <div>
      {/* 헤더 */}
      <header>
        <p className="label">DASHBOARD</p>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl leading-tight">
          수고하셨어요, {nickname}님
        </h1>
        <p className="mt-3 text-sm text-ink-600 dark:text-ink-300 font-sans">
          오늘 만난 책 한 줄, 남겨두세요
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-5 text-sm font-sans">
            <span className="text-ink-500 dark:text-ink-400">
              오늘 <span className="text-ink-900 dark:text-ink-100 tabular-nums">{recordCounts.today}</span>건
            </span>
            <span className="text-ink-500 dark:text-ink-400">
              이번 주 <span className="text-ink-900 dark:text-ink-100 tabular-nums">{recordCounts.week}</span>건
            </span>
            {todayStats.pages > 0 && (
              <span className="text-ink-500 dark:text-ink-400">
                오늘 <span className="text-ink-900 dark:text-ink-100 tabular-nums">{todayStats.pages}</span>쪽
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShareOpen(true)}
              disabled={shareTemplates.length === 0}
              className="btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              title={shareTemplates.length === 0 ? '공유할 기록이 아직 없어요' : 'X에 공유'}
            >
              <Share2 size={14} strokeWidth={1.5} />
              공유
            </button>
            <button onClick={onOpenQuickRecord} className="btn-ghost text-xs">
              <PenLine size={14} strokeWidth={1.5} />
              지금 기록 남기기
            </button>
            <Link to="/add" className="btn-primary text-xs">
              <Plus size={14} strokeWidth={1.5} />
              책 추가하기
            </Link>
          </div>
        </div>
      </header>

      {/* AI 코칭 */}
      {!coaching.dismissed && (coaching.message || coaching.loading) && (
        <div className="mt-10">
          <CoachingCard
            message={coaching.message}
            loading={coaching.loading}
            onDismiss={coaching.dismissCoaching}
            onRefresh={coaching.refresh}
          />
        </div>
      )}

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-x-12 gap-y-14">
        {/* 읽고 있는 책 */}
        <section>
          <div className="flex items-baseline justify-between border-b border-ink-200 dark:border-ink-800 pb-4">
            <h2 className="font-display text-2xl">읽고 있는 책</h2>
            <span className="label">IN PROGRESS</span>
          </div>

          {readingBooks.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-ink-600 dark:text-ink-300 font-sans">
                아직 읽고 있는 책이 없어요
              </p>
              <Link to="/add" className="btn-ghost mt-5 text-xs">
                <Plus size={14} strokeWidth={1.5} />
                첫 책 추가하기
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-ink-100 dark:divide-ink-900">
              {readingBooks.map(book => (
                <li key={book.id} className="py-5">
                  <div className="flex items-center gap-4">
                    {/* 책 정보 — 클릭하면 상세로. 진도 입력은 링크 밖에 둔다 */}
                    <Link
                      to={`/book/${book.id}`}
                      className="flex min-w-0 flex-1 items-center gap-5 transition-opacity hover:opacity-70"
                    >
                      {book.imageUrl ? (
                        <img
                          src={book.imageUrl}
                          alt=""
                          className="w-14 h-20 object-cover rounded-sm shrink-0"
                        />
                      ) : (
                        <span className="w-14 h-20 rounded-sm shrink-0 border border-ink-200 dark:border-ink-800" />
                      )}

                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg truncate">{book.title}</h3>
                        <p className="mt-0.5 text-sm text-ink-600 dark:text-ink-300 truncate font-sans">
                          {book.author}
                        </p>
                      </div>
                    </Link>

                    <ProgressQuickInput
                      book={book}
                      onValueChange={v => setDrafts(prev => ({ ...prev, [book.id]: v }))}
                    />

                    <Link
                      to={`/book/${book.id}`}
                      className="shrink-0 p-1 text-ink-400 dark:text-ink-500 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
                      aria-label={`${book.title} 상세`}
                    >
                      <ArrowRight size={16} strokeWidth={1.5} />
                    </Link>
                  </div>

                  {/* 페이스는 링크 바깥에 둔다 (안에 링크가 있어 중첩되면 안 된다) */}
                  <div className="mt-3 sm:pl-[76px]">
                    <PaceLine book={book} unitControl={<PageModeSwitch book={book} />} />
                    {/* 오늘 읽은 쪽이 있으면 인증 문구가 바로 이 자리에 뜬다 */}
                    <ReadingCertification
                      book={book}
                      extraPages={drafts[book.id] || 0}
                      className="mt-3"
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 최근 독서 기록 */}
        <section>
          <div className="flex items-baseline justify-between border-b border-ink-200 dark:border-ink-800 pb-4">
            <h2 className="font-display text-2xl">최근 독서 기록</h2>
            <span className="label">RECENT</span>
          </div>
          <RecentRecordsFeed records={records} books={books} />
        </section>
      </div>

      <ShareModal
        open={shareOpen}
        title="독서 공유"
        templates={shareTemplates}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
};

export default Home;
